import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { DropdownOption } from "../../../components";
import { UserProviderCalendarService } from "../../../services";
import { getUserInfo, RootState } from "../../../app";
import { setSelectedUser, setSelectedLocation } from "../../../app/store"; 
import { UserProviderInfoModel } from "../../../data/entities";
import { showErrorMessage } from "../../../utils";

export function useCalendarUserSelection() {
  const userProviderCalendarService = useMemo(() => new UserProviderCalendarService(), []);

  const dispatch = useDispatch();
  const currentUserId = useSelector(getUserInfo())?.id;
  const cachedPreferences = useSelector((state: RootState) => 
    currentUserId ? state.calendarPreferences.preferences[currentUserId.toString()] : undefined 
  );

  const [accessibleUsers, setAccessibleUsers] = useState<UserProviderInfoModel[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const isInitialMount = useRef(true);

  // Dropdown state
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [userOptions, setUserOptions] = useState<DropdownOption[]>([]);

  const currentUserDetails = useMemo(() => {
    if (!selectedUserId) return [];
    const user = accessibleUsers.find(u => u.userId.toString() === selectedUserId);
    return user?.details ?? [];
  }, [accessibleUsers, selectedUserId]);


  const locationOptions = useMemo(() => {
    const baseOptions = [
      { key: "", value: "", text: "All Locations" }
    ];

    if (!selectedUserId || !isInitialized) {
      return baseOptions;
    }

    const selectedUser = accessibleUsers.find(u => u.userId.toString() === selectedUserId);
    if (!selectedUser) {
      return baseOptions;
    }

    const uniqueLocations = new Map<number, { key: number; value: string; text: string; code: string }>();

    selectedUser.details.forEach(detail => {
      if (!uniqueLocations.has(detail.locationId)) {
        uniqueLocations.set(detail.locationId, {
          key: detail.locationId,
          value: detail.locationId.toString(),
          text: detail.locationName,
          code: detail.businessCode ?? "",
        });
      }
    });

    const userLocations = Array.from(uniqueLocations.values());
    const sortedUserLocations = userLocations.sort((a, b) => a.text.localeCompare(b.text));

    return [...baseOptions, ...sortedUserLocations];
  }, [selectedUserId, accessibleUsers, isInitialized]);

  const filteredDetails = useMemo(() => {
    if (!selectedLocationId || selectedLocationId === "") {
      return currentUserDetails;
    }
    return currentUserDetails.filter(detail => detail.locationId.toString() === selectedLocationId);
  }, [currentUserDetails, selectedLocationId]);

  const lastFetchRef = useRef<{ userId: string; locationId: string } | null>(null);

  // Fetch users and their locations
  useEffect(() => {
    const initializeData = async () => {
      try {
        const allUsers = await userProviderCalendarService.getUserProvidersByUserIdAsync();

        if (!allUsers || allUsers.length === 0) {
          setIsInitialized(true);
          isInitialMount.current = false;
          return;
        }

        // Inactive providers are hidden from the calendar and its dropdowns
        const users = allUsers.map(user => ({
          ...user,
          details: user.details?.filter(d => d.isActive) ?? [],
        }));

        setAccessibleUsers(users);

        const userOpts = users.map(user => ({
          text: user.displayName,
          value: user.userId.toString(),
          email: user.email,
        })).sort((a, b) => a.text.localeCompare(b.text));
        
        setUserOptions(userOpts);

        const userIdToSelect = cachedPreferences?.selectedUserId || currentUserId?.toString() || ""; 
        const selectedUser = users.find(u => u.userId.toString() === userIdToSelect);
        
        let locationIdToSelect = "";
        
        if (selectedUser) {
          const uniqueLocations = new Map<number, string>();
          selectedUser.details.forEach(detail => {
            if (!uniqueLocations.has(detail.locationId)) {
              uniqueLocations.set(detail.locationId, detail.locationId.toString());
            }
          });
          
          const userLocationIds = Array.from(uniqueLocations.values());
          
          if (cachedPreferences?.selectedLocationId) {
            const locationExists = selectedUser.details.some(
              d => d.locationId.toString() === cachedPreferences.selectedLocationId
            );
            if (locationExists) {
              locationIdToSelect = cachedPreferences.selectedLocationId;
            }
          }
          
          // If no cached location and only one location, auto-select it
          if (!locationIdToSelect && userLocationIds.length === 1) {
            locationIdToSelect = userLocationIds[0];
          }
        }
        
        setSelectedUserId(userIdToSelect);
        setSelectedLocationId(locationIdToSelect);
        
        setIsInitialized(true);
        isInitialMount.current = false;

      } catch (error) {
        showErrorMessage("Error loading users");
        setIsInitialized(true);
        isInitialMount.current = false;
      }
    };
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  // Update selected location when user changes
  useEffect(() => {
    if (!isInitialized || isInitialMount.current) return;
    
    if (!selectedUserId) {
      setSelectedLocationId("");
      return;
    }

    const selectedUser = accessibleUsers.find(u => u.userId.toString() === selectedUserId);
    if (!selectedUser) {
      setSelectedLocationId("");
      return;
    }

    const uniqueLocations = new Map<number, string>();
    selectedUser.details.forEach(detail => {
      if (!uniqueLocations.has(detail.locationId)) {
        uniqueLocations.set(detail.locationId, detail.locationId.toString());
      }
    });
    const userLocationIds = Array.from(uniqueLocations.values());

    // Determine new location
    const hasCachedLocation = cachedPreferences?.selectedLocationId;
    const cachedLocationValid = hasCachedLocation && userLocationIds.includes(hasCachedLocation);
    
    let newLocationId = "";
    
    if (cachedLocationValid && hasCachedLocation) {
      newLocationId = hasCachedLocation;
    } else if (userLocationIds.length === 1) {
      newLocationId = userLocationIds[0];
    }
    
    setSelectedLocationId(newLocationId);
    
    if (currentUserId) {
      dispatch(
        setSelectedLocation({
          userId: currentUserId.toString(),
          selectedLocationId: newLocationId,
        })
      );
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, isInitialized, accessibleUsers, dispatch, currentUserId, cachedPreferences]);


  const handleSelectUser = useCallback((e: DropDownListChangeEvent) => {
    const value = e.value;
    if (!value) return;

    const newUserId = value.value;
    
    setSelectedUserId(newUserId);

    if (currentUserId) {
      dispatch(
        setSelectedUser({
          userId: currentUserId.toString(),
          selectedUserId: newUserId,
        })
      );
    }
  }, [dispatch, currentUserId]);

  const handleSelectLocation = useCallback((e: DropDownListChangeEvent) => {
    const value = e.value;
    const newLocationId = value.value;
    
    setSelectedLocationId(newLocationId);
    
    if (currentUserId) {
      dispatch(
        setSelectedLocation({
          userId: currentUserId.toString(),
          selectedLocationId: newLocationId,
        })
      );
    }
  }, [dispatch, currentUserId]);

  const showUserDefaultItem = !userOptions.some(user => user.value === selectedUserId);

  return {
    selectedUserId,
    selectedLocationId,
    userOptions,
    locationOptions,
    isInitialized,
    accessibleUsers,
    currentUserDetails,
    filteredDetails,
    lastFetchRef,
    showUserDefaultItem,
    handleSelectUser,
    handleSelectLocation,
  };
}